import { useEffect, useState } from "react";
import {
  FlagCountResponse,
  GroupCount,
  ItemWithMetaData,
  TopicCount,
} from "../../api/Items";
import { useFamaFlags } from "../../api/FamaFlags";
import {
  ChartBarIcon,
  DocumentTextIcon,
  FlagIcon,
} from "@heroicons/react/24/outline";
import { classNames } from "../../utils";
import ReportsSummaryChartFlags from "./ReportsSummaryChartFlags";
import ReportsSummaryChartFlagsPercentile from "./ReportsSummaryChartFlagsPercentile";
import {
  useGetAnalyticsData,
  useGetReportAnalyticsCharts,
} from "../../api/CompaniesApi";
import LoadingSpinner from "../../shared-components/LoadingSpinner";

import { ReportSummaryHeaderNewFlags } from "./ReportSummaryHeaderNewFlags";
import { ReportsSummaryHeaderNoStats } from "./ReportsSummaryHeaderNoStats";
import ReportsSummaryHeaderFlagDescriptions from "./ReportsSummaryHeaderFlagDescriptions";
import SummaryHeaderQuestionMarkTitle from "./SummaryHeaderQuestionMarkTitle";

type Props = {
  flagsCount: FlagCountResponse | undefined;
  /** Full (all item types) flag counts for analytics API; defaults to flagsCount when omitted */
  analyticsFlagsCount?: FlagCountResponse | undefined;
  donutChartValues:
    | { color: string; label: string; value: number }[]
    | undefined;
  flagAmounts: { [key: string]: number } | undefined;
  profileList: ItemWithMetaData[] | undefined;
  keywordCount: GroupCount | undefined;
  topicCount?: TopicCount;
  companyUUID: string;
  hasReruns: boolean;
  hideTabs?: boolean;
};

export const ReportsSummaryHeader = ({
  flagsCount,
  analyticsFlagsCount,
  donutChartValues,
  flagAmounts,
  profileList,
  keywordCount,
  topicCount,
  companyUUID,
  hasReruns,
  hideTabs,
}: Props) => {
  const famaFlags = useFamaFlags();
  if (!donutChartValues) {
    return <LoadingSpinner />;
  }
  if (famaFlags.useStatsInSummary) {
    return (
      <ReportsSummaryHeaderTabs
        flagsCount={flagsCount}
        analyticsFlagsCount={analyticsFlagsCount}
        donutChartValues={donutChartValues}
        flagAmounts={flagAmounts}
        profileList={profileList}
        keywordCount={keywordCount}
        topicCount={topicCount}
        companyUUID={companyUUID}
        hasReruns={hasReruns}
        hideTabs={hideTabs}
      />
    );
  }

  return (
    <ReportsSummaryHeaderNoStats
      flagsCount={flagsCount}
      donutChartValues={donutChartValues}
      flagAmounts={flagAmounts ?? {}}
      profileList={profileList}
      keywordCount={keywordCount}
      topicCount={topicCount}
      companyUUID={companyUUID}
    />
  );
};

export const ReportsSummaryHeaderTabs = ({
  flagsCount,
  analyticsFlagsCount,
  donutChartValues,
  flagAmounts,
  profileList,
  keywordCount,
  topicCount,
  companyUUID,
  hasReruns,
  hideTabs,
}: Props) => {
  const flagsCountForAnalytics = analyticsFlagsCount ?? flagsCount;
  const [selectedTab, setSelectedTab] = useState<
    "summary" | "flag_descriptions" | "analytics"
  >("summary");

  const {
    mutate: getAnalyticsData,
    data: analyticsData,
    isLoading: isLoadingAnalyticsData,
  } = useGetAnalyticsData();

  const { data: reportAnalyticsSettings } =
    useGetReportAnalyticsCharts(companyUUID);
  const showAnalytics =
    reportAnalyticsSettings?.report_analytics_charts ?? false;

  useEffect(() => {
    if (!showAnalytics && selectedTab === "analytics") {
      setSelectedTab("summary");
    }
  }, [selectedTab, showAnalytics]);

  useEffect(() => {
    if (companyUUID && showAnalytics) {
      let countFlags = 0;
      flagsCountForAnalytics?.behaviors.groups.forEach((group) => {
        // COMMUNITY is not included in the adverse flag count consider before updating
        if (group.name !== "COMMUNITY") {
          countFlags += group.count;
        }
      });
      getAnalyticsData({
        company_uuid: companyUUID,
        candidate_flags: countFlags,
      });
    }
  }, [
    getAnalyticsData,
    companyUUID,
    showAnalytics,
    flagsCountForAnalytics?.behaviors.groups,
  ]);

  return (
    <div>
      {hideTabs ? null : (
        <div className="print:hidden flex gap-6 text-gray-500 border-b-2 border-gray-200 mb-4">
          <div
            className={classNames(
              "flex items-center gap-2 cursor-pointer pb-3",
              selectedTab === "summary"
                ? "text-green-600 border-b-2 border-green-600"
                : ""
            )}
            onClick={() => setSelectedTab("summary")}
          >
            <DocumentTextIcon width={24} height={24} />
            Summary
          </div>
          <div
            className={classNames(
              "flex items-center gap-2 cursor-pointer pb-3",
              selectedTab === "flag_descriptions"
                ? "text-green-600 border-b-2 border-green-600"
                : ""
            )}
            onClick={() => setSelectedTab("flag_descriptions")}
          >
            <FlagIcon width={24} height={24} /> Flag Descriptions
          </div>
          {showAnalytics && (
            <div
              className={classNames(
                "flex items-center gap-2 cursor-pointer pb-3",
                selectedTab === "analytics"
                  ? "text-green-600 border-b-2 border-green-600"
                  : ""
              )}
              onClick={() => setSelectedTab("analytics")}
            >
              <ChartBarIcon width={24} height={24} /> Analytics
            </div>
          )}
        </div>
      )}
      <div className="w-full overflow-hidden">
        {selectedTab === "summary" && (
          <ReportSummaryHeaderNewFlags
            flagsCount={flagsCount}
            donutChartValues={donutChartValues}
            flagAmounts={flagAmounts}
            profileList={profileList}
            keywordCount={keywordCount}
            topicCount={topicCount}
            companyUUID={companyUUID}
          />
        )}
        {selectedTab === "flag_descriptions" && (
          <div className="border rounded-md p-4 text-gray-600 mb-4">
            <ReportsSummaryHeaderFlagDescriptions
              flagsCount={flagsCount}
              keywordCount={keywordCount}
            />
          </div>
        )}
        {selectedTab === "analytics" && (
          <div className="border rounded-md p-4 text-gray-600 mb-4 ">
            <div className="mb-4 text-lg font-bold">
              Flagged Posts Per Report
            </div>
            <p className="mb-6">
              This graph shows this person’s flag count in relation to other
              people screened from your organization. While a higher count might
              indicate poor judgment about what is publicly appropriate to share
              on social media platforms and can pose potential reputation risk,
              it does not imply a lack of skill, competence, intent, or
              automatic disqualification. This data point should be use in
              conjunction with the detailed misconduct report and consider
              context, severity, recency, and relevance to your company's
              screening criteria.
            </p>
            <div className="print:mt-0 flex items-center mb-6  w-full gap-4 print:flex-col flex-col 2xl:flex-row">
              <div className="print:w-full w-full flex-1 flex flex-col items-center justify-center border-gray-300 border-2 p-3 rounded-lg">
                <SummaryHeaderQuestionMarkTitle
                  text="Misconduct Flag Count"
                  tooltipText="Each dot represents a person screened and the total number of adverse flags in the report. For example, a person with a total of 12 adverse flags shows on the graph indicated by the red line."
                />
                <div className="flex flex-row gap-4 p-2 rounded-md print:mt-0 print:text-xs">
                  <div className="flex items-center gap-2">
                    <div className="h-2 bg-[#FF5D22] w-6"></div>
                    This Person
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 bg-blue-700 w-6"></div>
                    Other People
                  </div>
                </div>
                <div className="w-full flex flex-row justify-between items-center print:mb-0 print:h-40 h-52 xl:h-72  gap-4 print:mt-0 mt-3">
                  <div className="flex-1 flex justify-center items-center h-full  p-2 print:p-0">
                    <ReportsSummaryChartFlags analyticsData={analyticsData} />
                  </div>
                </div>
              </div>
              <div className="print:w-full w-full flex-1 flex flex-col items-center justify-center border-gray-300 border-2 p-3 rounded-lg">
                <SummaryHeaderQuestionMarkTitle
                  text="Percentile"
                  tooltipText="Compares the person to all of your organization’s screenings within the last 365 days. For example, a person in the 10th percentile has a number of adverse flags that is greater than or equal to 10% of the people screened in your  organization."
                />
                <div className="flex flex-row gap-4  p-2 rounded-md print:mt-0 print:text-xs">
                  <div className="flex items-center gap-2">
                    <div className="h-2 bg-[#FF5D22] w-6"></div>
                    This Person
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 bg-blue-700 w-6"></div>
                    Other People
                  </div>
                </div>
                <div
                  className={classNames(
                    "w-full flex flex-row justify-between items-center print:mb-0  h-56 xl:h-72 gap-4 print:mt-0 mt-3 overflow-hidden",
                    hasReruns ? "print:h-[10rem]" : "print:h-[14rem]"
                  )}
                >
                  <div className="print:block flex-1 flex justify-center items-center h-full print:p-0 p-2">
                    <ReportsSummaryChartFlagsPercentile
                      analyticsData={analyticsData}
                    />
                  </div>
                </div>
              </div>
            </div>
            {isLoadingAnalyticsData && <LoadingSpinner />}

            {!isLoadingAnalyticsData && (
              <>
                <div className="text-sm ">
                  <SummaryHeaderQuestionMarkTitle
                    text="People Screened"
                    tooltipText="Shows the number and percent of your organization’s candidates who have more and fewer adverse flags than the candidate currently in review."
                  />

                  <div className="flex flex-row bg-gray-300 text-center font-bold py-4">
                    <div className="w-1/3"></div>
                    <div className="w-1/3">
                      Number of People in This Data Set
                    </div>
                    <div className="w-1/3">Percent of Total People</div>
                  </div>
                  <div className="flex flex-row bg-gray-100 py-4">
                    <div className="w-1/3">
                      <div className="grid grid-cols-3">
                        <div></div>
                        <div className="text-left whitespace-nowrap">
                          Lower than this person
                        </div>
                        <div></div>
                      </div>
                    </div>
                    <div className="w-1/3 text-center">
                      {analyticsData?.comparison.candidates_with_fewer.count}
                    </div>
                    <div className="w-1/3 text-center">
                      {
                        analyticsData?.comparison.candidates_with_fewer
                          .percentage
                      }
                      %
                    </div>
                  </div>
                  <div className="flex flex-row bg-gray-100 py-4">
                    <div className="w-1/3">
                      <div className="grid grid-cols-3">
                        <div></div>
                        <div className="text-left whitespace-nowrap">
                          Same as this person
                        </div>
                        <div></div>
                      </div>
                    </div>
                    <div className="w-1/3 text-center">
                      {analyticsData?.comparison.candidates_with_same.count}
                    </div>
                    <div className="w-1/3 text-center">
                      {
                        analyticsData?.comparison.candidates_with_same
                          .percentage
                      }
                      %
                    </div>
                  </div>
                  <div className="flex flex-row bg-gray-100 py-4">
                    <div className="w-1/3">
                      <div className="grid grid-cols-3">
                        <div></div>
                        <div className="text-left whitespace-nowrap">
                          Higher than this person
                        </div>
                        <div></div>
                      </div>
                    </div>
                    <div className="w-1/3 text-center">
                      {analyticsData?.comparison.candidates_with_more.count}
                    </div>
                    <div className="w-1/3 text-center">
                      {
                        analyticsData?.comparison.candidates_with_more
                          .percentage
                      }
                      %
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-4 text-center">
                  Trends in misconduct are for people screened in last 365 days
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
